import { Text, useDisclosure } from '@chakra-ui/react';
import { CreditCardIcon } from '@heroicons/react/outline';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import {
  FundraisingDocumentType,
  MergedHoldingMethodStep,
  SubscriptionStepStatus,
  useGetSubscriptionQuery,
  useValidateHoldingMethodMutation,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';

import { SubscriptionStep } from '../../SubscriptionStep';
import { HoldingMethodModal } from './HoldingMethodModal';

type HoldingMethodTypeMessage = keyof Messages['HoldingMethodTypeValue'];

type Props = {
  subscriptionStep: MergedHoldingMethodStep;
  isDisabled: boolean;
};

export function HoldingMethodStep({ subscriptionStep, isDisabled }: Props): JSX.Element {
  const i18nSubscription = useTranslations('Subscription');
  const i18nHoldingMethodTypeValue = useTranslations('HoldingMethodTypeValue');

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { subscriptionId = '' } = useParams();

  const [validateHoldingMethod] = useValidateHoldingMethodMutation();

  const { data, refetch } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });
  const subscription = data?.subscription;
  const holdingMethod = subscription?.holdingMethod;

  const isStepValidated = subscriptionStep.status === SubscriptionStepStatus.VALID;
  const investAmount = subscription?.investAmount || 0;

  const subscriptionDocument = subscription?.data.mergedSteps?.find(
    (step) =>
      step.__typename === 'MergedDocumentSignatureStep' &&
      step.documentType === FundraisingDocumentType.SUBSCRIPTION,
  );
  const isSubscriptionDocumentGenerated =
    subscriptionDocument && subscriptionDocument.status !== SubscriptionStepStatus.NONE;

  const handleModalOpen = (): void => {
    // Prevent modal from opening once subscription document is generated
    if (!isSubscriptionDocumentGenerated) {
      onOpen();
    }
  };

  const handleModalSubmit = (): void => {
    if (!subscriptionId) return;

    validateHoldingMethod({
      variables: {
        validateHoldingMethodInput: {
          subscriptionId,
        },
      },
      onCompleted: () => {
        refetch();
        onClose();
      },
    });
  };

  return (
    <>
      <SubscriptionStep
        icon={CreditCardIcon}
        isDisabled={isDisabled}
        status={subscriptionStep.status}
        title={i18nSubscription('SupportSelection')}
        type={subscriptionStep.type}
        onClick={!isDisabled ? handleModalOpen : noop}
      >
        {isStepValidated && (
          <Text fontSize="sm">
            {i18nSubscription.rich('InvestAmount', {
              investAmount: formatNumberCurrency(investAmount),
              holdingMethod: i18nHoldingMethodTypeValue(holdingMethod as HoldingMethodTypeMessage),
              important: (chunks) => <Text as="b">{chunks}</Text>,
            })}
          </Text>
        )}
      </SubscriptionStep>
      <HoldingMethodModal
        isOpen={isOpen}
        subscriptionStep={subscriptionStep}
        onClose={onClose}
        onSubmit={handleModalSubmit}
      />
    </>
  );
}
